import { Fragment, useCallback, useState } from "react";

import { Empty, FoldablePanel, Note } from "./Panel";
import { PnLShape } from "./PnLShape";
import { RateCell, RateHead } from "./RateCell";
import { StrategyLiveControls } from "./StrategyLiveControls";
import { StrategyRowActions } from "./StrategyRowActions";
import { useJournalShapes } from "../hooks/useShapes";
import { fragility, fragilityTone, rateTone, rateWord, shapeOf } from "../lib/shape";
import {
  useMispricedBreakdown,
  useMispricedSignals,
  useMispricedSummary,
} from "../hooks/useMispriced";
import type { MispricedGroup, MispricedSignal, MispricedSlice, ShadowShape } from "../api/types";
import type { MispricedEntry } from "../lib/mispriced";
import {
  adverse,
  edge,
  entriesOf,
  entryOutcome,
  lead,
  live,
  marketWord,
  pnlWord,
  ratio,
  roleLabel,
  roleLine,
  sliceLabel,
  slicesFor,
  sourceLabel,
  spreadCost,
  tone,
  verdict,
} from "../lib/mispriced";

/**
 * Расхождение модели и стакана.
 *
 * # Зачем панель на экране
 *
 * На карточке рынка справедливая цена исхода стоит рядом с серединой стакана, и
 * 2026-08-27 расхождение там было в десять центов при тейкерской комиссии в
 * девять десятых. Если оно настоящее — это первая за историю проекта кромка,
 * найденная в ЦЕНЕ, а не в отборе кошельков. Панель отвечает на «настоящее ли»,
 * и отвечает тремя парами чисел, а не одной прибылью:
 *
 *   - кромка на экране против кромки по цене НАЛИВА;
 *   - выставлено против НАЛИТО;
 *   - угадано среди всех против угаданного среди ИСПОЛНИВШИХСЯ.
 *
 * Строка сводки разворачивается в разрез своего счёта по минуте захода и цене
 * плана (BreakdownRow): сумма пары складывает несравнимые по безубытку строки,
 * и «из чего сложился счёт» по ней самой не видно.
 *
 * # Кнопки: у каких строк они есть и почему не у всех
 *
 * Порт постановки появился 2026-08-31 (ADR-0022), и вместе с ним на панели два
 * органа управления: переключатель живого режима над таблицей
 * (StrategyLiveControls) и «взять / мимо» по строке ленты (StrategyRowActions).
 *
 * Кнопки стоят ТОЛЬКО у тейкерских строк. Живой путь открыт одному тейкеру:
 * мейкерская строка получает живое правило `role` и ордером не станет ни при
 * каком нажатии, а кнопка по ней обещала бы действие, которого не существует, —
 * ровно то, от чего эта панель береглась, пока порта не было вовсе.
 *
 * Довод не стилевой. Тейкерская тень показала +$7311 за семь дней, но модель
 * исполнения в ней бумажная: ни одной неполной заливки на 10 322 строках. Живой
 * контроль из почти решённого говорит обратное — 4 из 5 FAK-ордеров по такому же
 * снимку промахнулись. Отделить кромку от оптимизма бумаги может только промах
 * FAK как измерение, и кнопки здесь стоят ради него.
 */
/**
 * Расхождение модели и стакана — сводка ретирированной подсистемы.
 *
 * # Почему свёрнута и почему внизу
 *
 * Сигнал признан неудачным решением оператора. Свёрнутая панель здесь не
 * косметика: под FoldablePanel тело НЕ МОНТИРУЕТСЯ, а значит два хука сводки
 * (`useMispricedSummary` и `useJournalShapes`) не заводят опроса вовсе — до сих
 * пор они ходили на сервер раз в пятнадцать секунд просто потому, что панель
 * была на экране.
 *
 * Ниже живых подсистем — по тому же доводу, по которому соседние сводки стоят
 * рядом: их читают В СРАВНЕНИИ друг с другом, и ретирированная между ними
 * заставляла бы сравнивать себя с работающими. Сводка при этом не удалена:
 * замер продолжает идти, и вернуться к нему можно одним разворотом.
 */
export function MispricedPanel({ hours, windowLen }: { hours: number; windowLen: number }) {
  return (
    <FoldablePanel
      title="Расхождение модели и стакана"
      subtitle="сигнал признан неудачным — замер идёт, панель свёрнута"
    >
      <MispricedPanelBody hours={hours} windowLen={windowLen} />
    </FoldablePanel>
  );
}

/**
 * Тело сводки — ОТДЕЛЬНЫЙ компонент, и это не косметика: см. разбор у
 * MispricedFeedBody. Свёрнутое тело не монтируется и опроса не заводит.
 *
 * Экспортируется РАДИ ТЕСТА: свёрнутую панель отрисовать в статическую разметку
 * нельзя — тела у неё нет по построению, — а утверждение теста («$/сут» стои́т в
 * шапке таблицы) про таблицу, то есть про тело. Рисовать обёртку и разворачивать
 * её кликом значило бы проверять FoldablePanel там, где спрашивают про колонку.
 */
export function MispricedPanelBody({ hours, windowLen }: { hours: number; windowLen: number }) {
  // Форма счёта — общим запросом на все журналы: границы корзин обязаны быть
  // одни, иначе гистограммы соседних панелей перестанут сравниваться.
  const shapes = useJournalShapes(hours, "mispriced", windowLen);
  const summary = useMispricedSummary(hours, windowLen);
  const groups = summary.data?.groups ?? [];
  // Развёрнутые строки сводки — по ключу пары. Тот же приём, что у состава
  // пула семей: Set в состоянии панели, а не флаг в строке, чтобы разворот
  // переживал перестройку списка при опросе.
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const toggle = useCallback((key: string) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  }, []);

  return (
    <>
      {/* Вердикт сводки — строкой в теле, а не подзаголовком панели: подзаголовок
          читается и свёрнутым, а данных для него у свёрнутой панели нет и быть не
          должно — за ними пришлось бы идти в сеть. */}
      {summary.data ? <Note>{verdict(groups)}</Note> : null}
      {/* Переключатель живого режима — над таблицей, по которой решение о нём
            и принимается. Стои́т ВЫШЕ ветки загрузки: режим читается из /status и
            от сводки не зависит, а спрятанный за «загрузка…» он исчезал бы ровно
            тогда, когда сводка не приехала. */}
        <StrategyLiveControls id="mispriced" />
        {!summary.data ? (
          <Empty>{summary.isError ? "сводка не загрузилась" : "загрузка…"}</Empty>
        ) : groups.length === 0 ? (
          <Empty>
            расхождений выше порога ещё не было: сигнал рождается, только когда
            модель расходится со стаканом сильнее, чем на комиссию с маржой
          </Empty>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-left text-[var(--color-muted)]">
                  <tr>
                    <th className="w-6 pb-2" />
                    <th className="pb-2 font-normal">источник</th>
                    <th className="pb-2 font-normal">роль</th>
                    <th className="pb-2 text-right font-normal">заявок</th>
                    <th className="pb-2 text-right font-normal">налито</th>
                    <th className="pb-2 text-right font-normal">кромка</th>
                    <th className="pb-2 text-right font-normal">на экране</th>
                    <th className="pb-2 text-right font-normal">угадано</th>
                    <th className="pb-2 text-right font-normal">из налитых</th>
                    <th className="pb-2 text-right font-normal">снято</th>
                    <th className="pb-2 text-right font-normal">жизнь</th>
                    <th className="pb-2 text-right font-normal">фора</th>
                    <th className="pb-2 text-right font-normal">счёт</th>
                    <RateHead />
                    <th className="pb-2 text-right font-normal">форма счёта</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((g) => {
                    const key = `${g.source}|${g.role}`;
                    return (
                      <Fragment key={key}>
                        <GroupRow
                          group={g}
                          hours={hours}
                          shape={shapeOf(shapes, key)}
                          expanded={open.has(key)}
                          onToggle={() => toggle(key)}
                        />
                        {open.has(key) && (
                          <BreakdownRow
                            group={g}
                            hours={hours}
                            windowLen={windowLen}
                            colSpan={15}
                          />
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Приговор форме — строкой на группу. Он отвечает на вопрос, который
                колонка «счёт» закрыть не может: держится ли плюс на кромке или на
                горстке сделок. У этой подсистемы ответ на живых данных был
                «хрупкий», и по сумме это было незаметно. */}
            <Note label="ставка и хрупкость — по строкам">
              <ul className="space-y-1 text-xs text-[var(--color-muted)]">
                {groups.map((g) => {
                  const shape = shapeOf(shapes, `${g.source}|${g.role}`);
                  return (
                    <li key={`${g.source}|${g.role}`}>
                      <b>
                        {sourceLabel(g.source)} · {roleLabel(g.role)}
                      </b>{" "}
                      — <b style={{ color: rateTone(shape, hours) }}>{rateWord(shape, hours)}</b>;{" "}
                      <span style={{ color: fragilityTone(shape) }}>{fragility(shape)}</span>.
                    </li>
                  );
                })}
              </ul>
            </Note>
            <Note label="как читать: кромка против экрана, налив и отбор">
              <b>«Кромка» — по цене налива, «на экране» — против середины стакана.</b>{" "}
              Разница между ними это половина спреда с комиссией, и на пятиминутках
              она бывает больше самой кромки: показанное преимущество тейкеру не
              достаётся. <b>«Налито» — не то же, что «заявок»:</b> мейкер стоит
              бесплатно и исполняется редко, и без этой колонки «сигналов много»
              читалось бы как «сделок много». <b>«Из налитых» против «угадано»</b> —
              прямая мера неблагоприятного отбора: меньшее число означает, что нас
              наливают охотнее там, где мы неправы. В скобках у счёта —{" "}
              <b>по скольким заявкам он посчитан</b>: ноль в деньгах бывает
              измеренный («стояли, никто не налил») и пустой («считать было нечем»).
              Ордеров подсистема не ставит и поставить не может: порта постановки у
              неё нет.
            </Note>
          </>
        )}
    </>
  );
}

/**
 * Лента теневых заявок: по строке на РЕШЕНИЕ, с обеими ролями рядом.
 *
 * # Почему не по строке на заявку
 *
 * В журнале единица — заявка, и это верно: тейкер и мейкер входят в разные
 * моменты и разными ценами. Но читают ленту по решениям — «модель разошлась со
 * стаканом по этой стороне, что дала каждая роль», — и роли здесь дополняют
 * друг друга, а не соперничают: там, где рынок уже платит больше нашей цены,
 * мейкером не встать, и весь ответ у тейкера; там, где тейкеру нечего забирать,
 * ответ у мейкера. Отказ одной из них — половина ответа, а не пустота, и
 * показывать её обязательно. Тот же приём, что в ленте почти решённого.
 *
 * Отдельным компонентом от сводки, чтобы страница могла поставить ленты НИЖЕ
 * обеих сводок: сравнивают сводки друг с другом, а не со своей лентой.
 */
export function MispricedFeed({ hours, windowLen }: { hours: number; windowLen: number }) {
  return (
    <FoldablePanel title="Теневые заявки" subtitle={`за ${hours} ч, по строке на решение`}>
      <MispricedFeedBody hours={hours} windowLen={windowLen} />
    </FoldablePanel>
  );
}

/**
 * Тело ленты — ОТДЕЛЬНЫЙ компонент, и это не косметика.
 *
 * Под FoldablePanel неразвёрнутое тело НЕ МОНТИРУЕТСЯ, а значит хук не заводит
 * опроса: свёрнутая лента молчит, а не просто прячет таблицу. Заявок здесь до
 * восьми на окно — самая частая лента на экране, и самая дорогая в опросе.
 */
function MispricedFeedBody({ hours, windowLen }: { hours: number; windowLen: number }) {
  const feed = useMispricedSignals(hours, undefined, windowLen);
  const entries = entriesOf(feed.data?.signals ?? []);

  return (
    <>
      {!feed.data ? (
        <Empty>{feed.isError ? "лента не загрузилась" : "загрузка…"}</Empty>
      ) : entries.length === 0 ? (
        <Empty>заявок за период не было</Empty>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-[var(--color-panel)]">
              <tr className="text-left text-[var(--color-muted)]">
                <th className="pb-2 font-normal">когда</th>
                <th className="pb-2 font-normal">источник</th>
                <th className="pb-2 font-normal">окно</th>
                <th className="pb-2 font-normal">исход</th>
                <th className="pb-2 text-right font-normal">модель</th>
                <th className="pb-2 text-right font-normal">фора</th>
                <th className="pb-2 font-normal">мейкер</th>
                <th className="pb-2 font-normal">тейкер</th>
                <th className="pb-2 font-normal">что стало</th>
                {/* Кнопки — последней колонкой и только у тейкера: живой путь
                    открыт одному ему, разбор в шапке файла. */}
                <th className="pb-2 font-normal">живой вход</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <FeedRow key={e.key} entry={e} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function GroupRow({
  group,
  shape,
  hours,
  expanded,
  onToggle,
}: {
  group: MispricedGroup;
  shape: ShadowShape | undefined;
  hours: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  const cost = spreadCost(group);
  const adv = adverse(group);
  return (
    <tr className="border-t border-[var(--color-border)]">
      {/* Разворот — тем же значком, что у состава пула семей: под строкой
          лежит разрез её счёта по минуте захода и цене плана. */}
      <td className="py-1">
        <button
          type="button"
          onClick={onToggle}
          className="text-[var(--color-muted)] hover:text-[var(--color-fg)]"
          aria-expanded={expanded}
          title="из чего сложился счёт: минута захода и цена плана"
        >
          {expanded ? "▾" : "▸"}
        </button>
      </td>
      <td className="py-1">{sourceLabel(group.source)}</td>
      <td className="py-1">{roleLabel(group.role)}</td>
      <td className="py-1 text-right num">{group.signals}</td>
      <td className="py-1 text-right num">{group.filled}</td>
      <td className={`py-1 text-right num ${toneClass(tone(group.edge_median))}`}>
        {edge(group.edge_median)}
      </td>
      <td
        className="py-1 text-right num text-[var(--color-muted)]"
        title={
          cost === null
            ? "сравнивать не с чем: стакан был односторонним"
            : `спред с комиссией съедает ${cost.toFixed(3)} на share`
        }
      >
        {edge(group.edge_mid_median)}
      </td>
      <td className="py-1 text-right num">{ratio(group.won, group.settled)}</td>
      <td
        className="py-1 text-right num"
        title={
          adv === null
            ? "исполнившихся заявок ещё не было"
            : `${adv >= 0 ? "+" : "−"}${Math.abs(adv).toFixed(1)} п.п. к доле среди всех — ` +
              "отрицательное значение и есть подпись неблагоприятного отбора"
        }
      >
        {ratio(group.won_filled, group.filled)}
      </td>
      <td className="py-1 text-right num">{group.cancelled}</td>
      <td className="py-1 text-right num">{live(group.live_median_ms)}</td>
      <td className="py-1 text-right num">{lead(group.lead_median_ms)}</td>
      <td className="py-1 text-right num">{pnlWord(group)}</td>
      {/* Удельный счёт — ВПРИТЫК к сумме: несравнимость подсистем живёт в
          колонке слева, и исправление обязано стоять рядом с ней. */}
      <RateCell shape={shape} hours={hours} />
      {/* Форма СЧЁТА, а не его пересказ: сумма слева отвечает «сколько»,
          гистограмма — «из чего». У этой подсистемы разница принципиальна: масса
          стоит в самой левой корзине («потеряли весь билет»), а плюс вытянут
          длинным правым хвостом. */}
      <td className="py-1 text-right">
        <PnLShape shape={shape} />
      </td>
    </tr>
  );
}

/**
 * Разрез счёта развёрнутой пары: из чего сложилось её «счёт … по …».
 *
 * # Зачем две оси
 *
 * Сумма пары складывает заходы за пять минут до конца с заходами на последних
 * секундах и аски по 0.45 с асками по 0.95 — а безубыток и базовая линия у этих
 * строк разные. У почти решённого весь плюс лежал в асках дешевле 0.80, и по
 * итогу это было не видно; у смещённого края тот же вопрос закрывает разрез по
 * минуте. Здесь обе оси разом: минута захода (от конца окна — длительность окна
 * журнал не хранит, а решает близость к резолву) и цена плана десятыми долями.
 *
 * # Почему хук живёт здесь, а не в панели
 *
 * Компонент монтируется только под РАЗВЁРНУТОЙ строкой, а значит свёрнутая
 * панель разреза не опрашивает вовсе — тот же приём и тот же довод, что у тел
 * FoldablePanel. Ручка одна на все пары, и две развёрнутые строки складываются
 * в один запрос ключом TanStack.
 */
function BreakdownRow({
  group,
  hours,
  windowLen,
  colSpan,
}: {
  group: MispricedGroup;
  hours: number;
  windowLen: number;
  colSpan: number;
}) {
  const breakdown = useMispricedBreakdown(hours, windowLen);
  const slices = breakdown.data?.slices;
  return (
    <tr className="border-t border-[var(--color-border)]">
      <td colSpan={colSpan} className="bg-[var(--color-bg)] px-3 py-2">
        {!slices ? (
          <Empty>{breakdown.isError ? "разрез не загрузился" : "загрузка…"}</Empty>
        ) : (
          <div className="flex flex-wrap gap-x-12 gap-y-3">
            <SliceTable
              title="по минуте захода"
              rows={slicesFor(slices, group.source, group.role, "minute")}
            />
            <SliceTable
              title="по цене плана"
              rows={slicesFor(slices, group.source, group.role, "price")}
            />
          </div>
        )}
      </td>
    </tr>
  );
}

/**
 * Одна ось разреза. Колонки — те же вопросы, что у сводки, и в том же порядке:
 * заявок, налито, угадано, счёт со знаменателем. Новых величин разрез не
 * вводит намеренно — он раскладывает известные, и чужая колонка здесь означала
 * бы число, которого нет наверху и которое не с чем сверить.
 */
function SliceTable({ title, rows }: { title: string; rows: MispricedSlice[] }) {
  return (
    <div>
      <h4 className="mb-1 text-[11px] font-medium text-[var(--color-muted)]">{title}</h4>
      {rows.length === 0 ? (
        <p className="text-[11px] text-[var(--color-muted)]">корзин за период нет</p>
      ) : (
        <table className="text-[11px]">
          <thead className="text-left text-[var(--color-muted)]">
            <tr>
              <th className="pb-1 pr-4 font-normal">корзина</th>
              <th className="pb-1 pr-4 text-right font-normal">заявок</th>
              <th className="pb-1 pr-4 text-right font-normal">налито</th>
              <th className="pb-1 pr-4 text-right font-normal">угадано</th>
              <th className="pb-1 text-right font-normal">счёт</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.bucket} className="border-t border-[var(--color-border)]">
                <td className="whitespace-nowrap py-0.5 pr-4">{sliceLabel(s)}</td>
                <td className="num py-0.5 pr-4 text-right">{s.signals}</td>
                <td className="num py-0.5 pr-4 text-right">{s.filled}</td>
                <td className="num py-0.5 pr-4 text-right">{ratio(s.won, s.settled)}</td>
                <td className="num whitespace-nowrap py-0.5 text-right">{pnlWord(s)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function FeedRow({ entry }: { entry: MispricedEntry }) {
  const when = new Date(entry.detectedAt);
  return (
    <tr className="border-t border-[var(--color-border)] align-top">
      <td className="num py-1.5">{when.toISOString().slice(11, 19)}</td>
      <td className="py-1.5">{sourceLabel(entry.source)}</td>
      <td className="py-1.5">{marketWord(entry)}</td>
      <td className="py-1.5">{entry.outcome}</td>
      <td className="num py-1.5 text-right">{entry.fair}</td>
      <td className="num py-1.5 text-right">{lead(entry.leadMs)}</td>
      <RoleCell signal={entry.maker} attempts={entry.makerAttempts} />
      <RoleCell signal={entry.taker} attempts={0} />
      <td className="py-1.5">{entryOutcome(entry)}</td>
      <td className="py-1.5">
        {/* Кнопки только по ТЕЙКЕРСКОЙ строке, и только когда она есть.
            Мейкерская строка живого пути не имеет: трейдер отвергнет команду по
            ней правилом `role`, а кнопка обещала бы обратное.

            Строки без тейкера («забирать было нечего») кнопок тоже не получают:
            брать нечего, и нажатие ушло бы в пустой стакан. */}
        {entry.taker && (
          <StrategyRowActions
            strategy="mispriced"
            signalID={entry.taker.id}
            windowEnd={entry.taker.window_end}
          />
        )}
      </td>
    </tr>
  );
}

/**
 * Одна роль в ленте: цена с кромкой сверху, подробность приглушённой строкой.
 *
 * «Не было» красится как отрицательное, а не серым прочерком: это РЕЗУЛЬТАТ
 * замера («тейкеру нечего было забирать», «мейкером не встать»), и серый его
 * читался бы как «ещё считаем».
 */
function RoleCell({
  signal,
  attempts,
}: {
  signal: MispricedSignal | undefined;
  attempts: number;
}) {
  const { head, note } = roleLine(signal, attempts);
  const color = signal ? toneClass(tone(signal.edge_exec)) : "text-[var(--color-ask)]";
  return (
    <td className="py-1.5">
      <div className={`num ${color}`}>{head}</div>
      {note !== "" && <div className="text-[11px] text-[var(--color-muted)]">{note}</div>}
    </td>
  );
}

/** Цвет кромки. Ноль приглушён, прочерк тоже: «сошлось» и «не считали» разные. */
function toneClass(t: "up" | "down" | "flat"): string {
  if (t === "up") return "text-[var(--color-bid)]";
  if (t === "down") return "text-[var(--color-ask)]";
  return "text-[var(--color-muted)]";
}
